import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import {
    Mic,
    MicOff,
    Video,
    VideoOff,
    Monitor,
    MonitorOff,
    Users,
    MessageSquare,
    Settings,
    Copy,
    Send,
    Radio,
    Square,
    UserPlus,
    MoreVertical,
    Maximize2,
    Volume2,
    VolumeX,
    Calendar as CalendarIcon,
    Clock
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icons mapping
const PlatformIcon = ({ platform, className }) => {
    const icons = {
        youtube: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
        ),
        facebook: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
        ),
        twitter: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
        ),
        linkedin: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
        ),
        twitch: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
            </svg>
        ),
        tiktok: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
        ),
        whatsapp: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
        ),
        meet: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C6.28 0 1.636 4.644 1.636 10.364c0 2.013.578 3.891 1.575 5.478L.545 24l8.389-2.636c1.538.89 3.32 1.4 5.066 1.4C19.72 22.764 24 18.084 24 12.364 24 5.644 18.72 0 12 0zm0 2.182c5.35 0 9.818 4.468 9.818 10.182 0 4.735-3.508 8.727-8.182 9.545l-.364.073-5.49 1.727 1.8-5.236-.29-.436A9.07 9.07 0 012.182 10.364c0-5.35 4.468-9.818 9.818-9.818zm-3.273 5.454v8.728h1.637V13.09h2.181l2.728 3.273h2.036l-2.945-3.49c1.4-.437 2.4-1.691 2.4-3.237 0-1.855-1.509-3.273-3.491-3.273H8.727v1.273zm1.637 1.273h2.909c1.018 0 1.854.69 1.854 1.636 0 .946-.836 1.637-1.854 1.637H10.364V8.909z"/>
            </svg>
        ),
        discord: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
            </svg>
        ),
        telegram: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
        ),
        slack: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
            </svg>
        ),
        zoom: (
            <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                <path d="M4.585 7.588a1.5 1.5 0 0 0-1.5 1.5v5.823a1.5 1.5 0 0 0 1.5 1.5h9.168a1.5 1.5 0 0 0 1.5-1.5V13.5l4.162 2.774a.75.75 0 0 0 1.17-.624V8.35a.75.75 0 0 0-1.17-.624l-4.162 2.774V9.088a1.5 1.5 0 0 0-1.5-1.5H4.585z"/>
            </svg>
        )
    };
    return icons[platform] || <Radio className={className} />;
};

export const Studio = () => {
    const { streamId } = useParams();
    const navigate = useNavigate();
    
    // Stream state
    const [stream, setStream] = useState(null);
    const [isLive, setIsLive] = useState(false);
    const [streamTitle, setStreamTitle] = useState('My Live Stream');
    const [destinations, setDestinations] = useState([]);
    const [selectedDestinations, setSelectedDestinations] = useState([]);
    
    // Scheduling state
    const [isScheduling, setIsScheduling] = useState(false);
    const [scheduledDate, setScheduledDate] = useState(null);
    const [scheduledTime, setScheduledTime] = useState('18:00');
    
    // Control state
    const [isMuted, setIsMuted] = useState(false);
    const [isCameraOn, setIsCameraOn] = useState(true);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    
    // UI state
    const [showChat, setShowChat] = useState(true);
    const [chatMessage, setChatMessage] = useState('');
    const [chatMessages, setChatMessages] = useState([
        { id: '1', sender_name: 'Viewer123', message: 'Hello everyone!', timestamp: new Date().toISOString() },
        { id: '2', sender_name: 'StreamFan', message: 'Excited for this stream!', timestamp: new Date().toISOString() },
        { id: '3', sender_name: 'TechLover', message: 'Great content as always!', timestamp: new Date().toISOString() },
    ]);
    const [viewerCount, setViewerCount] = useState(0);
    const [streamDuration, setStreamDuration] = useState(0);
    
    const chatEndRef = useRef(null);
    const durationInterval = useRef(null);

    useEffect(() => {
        fetchDestinations();
        if (streamId) {
            fetchStream(streamId);
        }
        
        return () => {
            if (durationInterval.current) {
                clearInterval(durationInterval.current);
            }
        };
    }, [streamId]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    const fetchDestinations = async () => {
        try {
            const res = await axios.get(`${API}/destinations`);
            setDestinations(res.data);
            setSelectedDestinations(res.data.filter(d => d.is_enabled).map(d => d.id));
        } catch (error) {
            console.error('Error fetching destinations:', error);
        }
    };

    const fetchStream = async (id) => {
        try {
            const res = await axios.get(`${API}/streams/${id}`);
            setStream(res.data);
            setStreamTitle(res.data.title);
            setIsLive(res.data.status === 'live');
            if (res.data.status === 'live') {
                startDurationTimer();
            }
        } catch (error) {
            console.error('Error fetching stream:', error);
        }
    };

    const startDurationTimer = () => {
        durationInterval.current = setInterval(() => {
            setStreamDuration(prev => prev + 1);
            setViewerCount(prev => Math.max(0, prev + Math.floor(Math.random() * 10) - 4));
        }, 1000);
    };

    const handleGoLive = async () => {
        if (selectedDestinations.length === 0) {
            toast.error('Please select at least one destination');
            return;
        }

        try {
            // Create stream if not exists
            let currentStream = stream;
            if (!currentStream) {
                const res = await axios.post(`${API}/streams`, {
                    title: streamTitle,
                    destinations: selectedDestinations,
                    recording_enabled: true,
                    chat_enabled: true
                });
                currentStream = res.data;
                setStream(currentStream);
            }

            // Start stream
            await axios.post(`${API}/streams/${currentStream.id}/start`);
            setIsLive(true);
            setViewerCount(Math.floor(Math.random() * 50) + 10);
            startDurationTimer();
            toast.success('You are now live!');
        } catch (error) {
            console.error('Error starting stream:', error);
            toast.error('Failed to start stream');
        }
    };

    const handleScheduleStream = async () => {
        if (selectedDestinations.length === 0) {
            toast.error('Please select at least one destination');
            return;
        }
        if (!scheduledDate) {
            toast.error('Please select a date');
            return;
        }

        try {
            const scheduledDateTime = new Date(scheduledDate);
            const [hours, minutes] = scheduledTime.split(':');
            scheduledDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            const res = await axios.post(`${API}/streams`, {
                title: streamTitle,
                destinations: selectedDestinations,
                scheduled_at: scheduledDateTime.toISOString(),
                recording_enabled: true,
                chat_enabled: true
            });
            
            setStream(res.data);
            setIsScheduling(false);
            toast.success(`Stream scheduled for ${format(scheduledDateTime, 'PPP')} at ${scheduledTime}`);
            navigate('/');
        } catch (error) {
            console.error('Error scheduling stream:', error);
            toast.error('Failed to schedule stream');
        }
    };

    const handleEndStream = async () => {
        if (!stream) return;
        
        try {
            await axios.post(`${API}/streams/${stream.id}/stop`);
            setIsLive(false);
            if (durationInterval.current) {
                clearInterval(durationInterval.current);
            }
            toast.success('Stream ended successfully');
            navigate('/');
        } catch (error) {
            console.error('Error ending stream:', error);
            toast.error('Failed to end stream');
        }
    };

    const toggleDestination = (destId) => {
        setSelectedDestinations(prev => 
            prev.includes(destId) 
                ? prev.filter(id => id !== destId)
                : [...prev, destId]
        );
    };

    const copyInviteLink = () => {
        const link = stream?.invite_link || `https://streamhub.app/join/${Math.random().toString(36).substr(2, 8)}`;
        navigator.clipboard.writeText(link);
        toast.success('Invite link copied!');
    };

    const sendChatMessage = (e) => {
        e.preventDefault();
        if (!chatMessage.trim()) return;
        
        const newMessage = {
            id: Date.now().toString(),
            sender_name: 'You (Host)',
            message: chatMessage,
            timestamp: new Date().toISOString(),
            is_highlighted: true
        };
        
        setChatMessages(prev => [...prev, newMessage]);
        setChatMessage('');
    };

    const formatDuration = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="studio-layout" data-testid="studio-page">
            {/* Main Stage Area */}
            <div className="flex flex-col bg-gray-900">
                {/* Video Preview */}
                <div className="flex-1 p-4">
                    <div className="video-tile h-full">
                        {/* Mock video preview */}
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                            {isCameraOn ? (
                                <div className="text-center">
                                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-4xl font-bold mb-4 mx-auto">
                                        JD
                                    </div>
                                    <p className="text-white/60 text-sm">Camera Preview</p>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <VideoOff className="w-16 h-16 text-white/30 mx-auto mb-2" />
                                    <p className="text-white/60 text-sm">Camera is off</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Live indicator */}
                        {isLive && (
                            <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500 px-3 py-1.5 rounded-lg" data-testid="live-indicator">
                                <span className="w-2 h-2 rounded-full bg-white animate-pulse-live" />
                                <span className="text-white font-semibold text-sm">LIVE</span>
                                <span className="text-white/80 text-sm ml-2">{formatDuration(streamDuration)}</span>
                            </div>
                        )}
                        
                        {/* Viewer count */}
                        {isLive && (
                            <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/50 px-3 py-1.5 rounded-lg">
                                <Users className="w-4 h-4 text-white" />
                                <span className="text-white text-sm">{viewerCount.toLocaleString()}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Controls Bar */}
                <div className="border-t border-white/10 p-4 bg-gray-900">
                    <div className="flex items-center justify-between">
                        {/* Left Controls */}
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setIsMuted(!isMuted)}
                                className={`control-btn ${isMuted ? 'control-btn-danger' : 'control-btn-default'}`}
                                data-testid="mute-btn"
                            >
                                {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                            </button>
                            <button
                                onClick={() => setIsCameraOn(!isCameraOn)}
                                className={`control-btn ${!isCameraOn ? 'control-btn-danger' : 'control-btn-default'}`}
                                data-testid="camera-btn"
                            >
                                {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
                            </button>
                            <button
                                onClick={() => setIsScreenSharing(!isScreenSharing)}
                                className={`control-btn ${isScreenSharing ? 'control-btn-active' : 'control-btn-default'}`}
                                data-testid="screen-share-btn"
                            >
                                {isScreenSharing ? <MonitorOff className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}
                            </button>
                        </div>

                        {/* Center - Go Live / Schedule Buttons */}
                        <div className="flex items-center gap-3">
                            {isLive ? (
                                <Button
                                    onClick={handleEndStream}
                                    className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 gap-2"
                                    data-testid="end-stream-btn"
                                >
                                    <Square className="w-5 h-5" />
                                    End Stream
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        onClick={handleGoLive}
                                        className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 gap-2"
                                        data-testid="go-live-btn"
                                    >
                                        <Radio className="w-5 h-5" />
                                        Go Live
                                    </Button>
                                    <Popover open={isScheduling} onOpenChange={setIsScheduling}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="border-white/20 text-white hover:bg-white/10 gap-2"
                                                data-testid="schedule-btn"
                                            >
                                                <CalendarIcon className="w-5 h-5" />
                                                Schedule
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-4 bg-gray-800 border-white/10" align="center">
                                            <div className="space-y-4">
                                                <h4 className="font-semibold text-white text-center">Schedule Stream</h4>
                                                <Calendar
                                                    mode="single"
                                                    selected={scheduledDate}
                                                    onSelect={setScheduledDate}
                                                    disabled={(date) => date < new Date()}
                                                    className="rounded-md border border-white/10"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <Clock className="w-4 h-4 text-white/60" />
                                                    <Input
                                                        type="time"
                                                        value={scheduledTime}
                                                        onChange={(e) => setScheduledTime(e.target.value)}
                                                        className="bg-white/5 border-white/10 text-white"
                                                        data-testid="schedule-time-input"
                                                    />
                                                </div>
                                                {scheduledDate && (
                                                    <p className="text-sm text-white/60 text-center">
                                                        {format(scheduledDate, 'PPP')} at {scheduledTime}
                                                    </p>
                                                )}
                                                <Button
                                                    onClick={handleScheduleStream}
                                                    className="w-full bg-primary hover:bg-primary/90"
                                                    disabled={!scheduledDate}
                                                    data-testid="confirm-schedule-btn"
                                                >
                                                    <CalendarIcon className="w-4 h-4 mr-2" />
                                                    Schedule Stream
                                                </Button>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </>
                            )}
                        </div>

                        {/* Right Controls */}
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowChat(!showChat)}
                                className={`control-btn ${showChat ? 'control-btn-active' : 'control-btn-default'}`}
                                data-testid="toggle-chat-btn"
                            >
                                <MessageSquare className="w-5 h-5" />
                            </button>
                            <button className="control-btn control-btn-default" data-testid="settings-btn">
                                <Settings className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Sidebar */}
            <div className={`border-l border-white/10 bg-gray-900 flex flex-col ${showChat ? '' : 'hidden lg:flex'}`}>
                {/* Stream Info */}
                <div className="p-4 border-b border-white/10">
                    <Input
                        value={streamTitle}
                        onChange={(e) => setStreamTitle(e.target.value)}
                        placeholder="Stream title"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-3"
                        disabled={isLive}
                        data-testid="stream-title-input"
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-white/20 text-white hover:bg-white/10 gap-2"
                        onClick={copyInviteLink}
                        data-testid="copy-invite-btn"
                    >
                        <UserPlus className="w-4 h-4" />
                        Invite Guests
                        <Copy className="w-3 h-3 ml-auto" />
                    </Button>
                </div>

                {/* Destinations */}
                <div className="p-4 border-b border-white/10">
                    <h3 className="text-sm font-semibold text-white mb-3">Broadcast To</h3>
                    <div className="space-y-2">
                        {destinations.map((dest) => (
                            <div
                                key={dest.id}
                                className="flex items-center justify-between p-2 rounded-lg bg-white/5"
                                data-testid={`destination-${dest.platform}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`platform-icon ${
                                        dest.platform === 'youtube' ? 'bg-red-500/20 text-red-500' :
                                        dest.platform === 'facebook' ? 'bg-blue-500/20 text-blue-500' :
                                        dest.platform === 'twitter' ? 'bg-sky-500/20 text-sky-500' :
                                        dest.platform === 'linkedin' ? 'bg-blue-700/20 text-blue-700' :
                                        dest.platform === 'twitch' ? 'bg-purple-500/20 text-purple-500' :
                                        dest.platform === 'tiktok' ? 'bg-pink-500/20 text-pink-500' :
                                        dest.platform === 'whatsapp' ? 'bg-green-500/20 text-green-500' :
                                        dest.platform === 'meet' ? 'bg-emerald-500/20 text-emerald-500' :
                                        dest.platform === 'discord' ? 'bg-indigo-500/20 text-indigo-500' :
                                        dest.platform === 'telegram' ? 'bg-sky-400/20 text-sky-400' :
                                        dest.platform === 'slack' ? 'bg-amber-500/20 text-amber-500' :
                                        dest.platform === 'zoom' ? 'bg-blue-500/20 text-blue-500' :
                                        'bg-white/10 text-white'
                                    }`}>
                                        <PlatformIcon platform={dest.platform} className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-white">{dest.name}</p>
                                        <p className="text-xs text-white/50">
                                            {dest.is_connected ? 'Connected' : 'Not connected'}
                                        </p>
                                    </div>
                                </div>
                                <Switch
                                    checked={selectedDestinations.includes(dest.id)}
                                    onCheckedChange={() => toggleDestination(dest.id)}
                                    disabled={!dest.is_connected || isLive}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Chat */}
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="p-3 border-b border-white/10 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Live Chat
                        </h3>
                        <span className="text-xs text-white/50">{chatMessages.length} messages</span>
                    </div>
                    <ScrollArea className="flex-1 p-3">
                        <div className="space-y-2">
                            {chatMessages.map((msg) => (
                                <div key={msg.id} className="chat-message">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/50 to-purple-500/50 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                                        {msg.sender_name.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`text-sm font-medium ${msg.is_highlighted ? 'text-primary' : 'text-white/80'}`}>
                                            {msg.sender_name}
                                        </p>
                                        <p className="text-sm text-white/60 break-words">{msg.message}</p>
                                    </div>
                                </div>
                            ))}
                            <div ref={chatEndRef} />
                        </div>
                    </ScrollArea>
                    <form onSubmit={sendChatMessage} className="p-3 border-t border-white/10">
                        <div className="flex gap-2">
                            <Input
                                value={chatMessage}
                                onChange={(e) => setChatMessage(e.target.value)}
                                placeholder="Send a message..."
                                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                                data-testid="chat-input"
                            />
                            <Button
                                type="submit"
                                size="icon"
                                className="bg-primary hover:bg-primary/90"
                                data-testid="send-chat-btn"
                            >
                                <Send className="w-4 h-4" />
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Studio;
